import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const data = useMemo(() => location.state || {}, [location.state]);

  const REDIRECT_SECONDS = 5;
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (countdown <= 0) {
      navigate("/thank-you", { state: data });
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate, data]);

  const progress = ((REDIRECT_SECONDS - countdown) / REDIRECT_SECONDS) * 100;

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-4">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl animate-pulse" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-cyan-200/40 blur-3xl animate-pulse" />
      <div className="pointer-events-none absolute top-1/3 right-10 h-40 w-40 rounded-full bg-yellow-200/30 blur-3xl" />

      {/* Confetti dots */}
      <ConfettiBurst />

      {/* Card */}
      <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl ring-1 ring-black/5 p-8 sm:p-10 max-w-md w-full text-center animate-[fadeInUp_0.6s_ease-out]">
        {/* Animated check */}
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center">
          <div className="absolute h-24 w-24 rounded-full bg-emerald-100 animate-ping opacity-60" />
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-300/50">
            <svg
              className="h-12 w-12 text-white drop-shadow"
              viewBox="0 0 52 52"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path
                d="M14 27 l8 8 l16 -18"
                style={{
                  strokeDasharray: 60,
                  strokeDashoffset: 60,
                  animation: "drawCheck 0.6s ease-out 0.3s forwards",
                }}
              />
            </svg>
          </div>
        </div>

        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
          Payment Successful!
        </h2>
        <p className="mt-2 text-gray-500">
          Your information has been saved securely.
        </p>

        {/* Details */}
        <div className="mt-6 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-white p-5 text-left text-sm space-y-3">
          {data.fullName && (
            <DetailRow label="Name" value={data.fullName} />
          )}
          {data.email && <DetailRow label="Email" value={data.email} />}
          {data.amount && (
            <DetailRow
              label="Amount"
              value={`₹${data.amount}`}
              highlight
            />
          )}
        </div>

        {/* Countdown */}
        <div className="mt-7">
          <p className="text-sm text-gray-600">
            Redirecting to thank-you page in{" "}
            <span className="font-bold text-emerald-600">{countdown}</span>s…
          </p>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-cyan-500 transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/thank-you", { state: data })}
          className="mt-6 w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3 rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all font-semibold shadow-lg shadow-emerald-300/40 hover:shadow-emerald-400/50 active:scale-[0.98]"
        >
          Continue Now
        </button>
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes confettiFall {
          0% { transform: translateY(-20vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span
        className={
          highlight
            ? "font-bold text-emerald-700 text-base"
            : "font-semibold text-gray-900"
        }
      >
        {value}
      </span>
    </div>
  );
}

function ConfettiBurst() {
  const colors = [
    "#10b981",
    "#06b6d4",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  const pieces = Array.from({ length: 40 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 2;
        const duration = 3 + Math.random() * 3;
        const size = 6 + Math.random() * 8;
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: "-10vh",
              width: `${size}px`,
              height: `${size * 0.4}px`,
              background: color,
              borderRadius: "2px",
              animation: `confettiFall ${duration}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}
